import classNames from 'classnames';
import { useCallback, useEffect, useRef, forwardRef, useImperativeHandle, useContext, useMemo, DragEventHandler, useState } from 'react';
import DruidWebchatContext, { DruidWebchatContextType } from './WebChatContext';
import { determineDirection, getSideRTLAware } from './utils/localeUtils';
import { useAutoExpand } from './hooks/useAutoExpand';
import { useContainerElementToggle } from './hooks/useContainerElementToggle';
import { Button, Overlay, Popover } from 'react-bootstrap';

import { WebChatOptions, webChatDefaultOptions as defaultOptions } from './WebChatOptions';

import loadable from '@loadable/component';


// import WebChat from './WebChat';
import './MinimizableWebChat.scss';

import '../fabric-icons-inline.css';
import { useDeterminedDirection } from './selectors/useDeterminedDirection';
import { mobileAndTabletcheck } from './utils/mobileAndTabletCheck';
import useStyleOptions from 'botframework-webchat/lib/hooks/useStyleOptions';
import { useMergedOptions } from './selectors/useMergedOptions';
import InlineSVG from 'svg-inline-react';
import FocusTrap from 'focus-trap-react';
import { useDraggableEffect } from './hooks/useDraggableEffect';
import { useWebChatRefsContext } from './hooks/useWebChatRefsContext';
import { Resizable } from 're-resizable';
import { FaTimes } from 'react-icons/fa';
// import { DruidAction } from './hooks/useWebChatReducer';
import DOMPurify from 'dompurify';

const LoadableWebchat = loadable(() => {
  return import(
    /* webpackChunkName: "modules" */
    './WebChat');
});


const MinimizableWebChat = forwardRef(({ initialOptions }: { initialOptions: Partial<WebChatOptions> }, ref) => {

  const [druidWebChatState, druidWebChatDispatch, , druidStyleToEmotionObject] = useContext<DruidWebchatContextType>(DruidWebchatContext);
  const { headerWasDragged } = useWebChatRefsContext();

  const { styleOptions: { accent, subtle, sendBoxButtonColorOnDisabled, sendBoxButtonColorOnHover, sendBoxButtonColorOnFocus, bubbleTextColor, paddingRegular, primaryFont, backgroundColor } = {},
    disabled,
    UI: {
      top, bottom, height, width, minWidth, chatBoxBorderRadius, chatBoxBottomRadius, chatBoxBorderColor, chatBoxBorderSize,
      transcriptFontSize, headerTextColor, closeButtonEnabled, floatRight, resizableEnabled,
      containerElement, draggableEnabled, fullScreenMode, accessibilityFeaturesEnabled, rootFontSize,
    } = {}
  } = useMergedOptions();

  const determinedDir = useDeterminedDirection();

  const webchatLoadedRef = useRef<boolean>(false);

  useAutoExpand();

  useContainerElementToggle({ initialOptions });

  useEffect(() => {
    containerElement
      // && druidWebChatState.loaded
      && containerElement instanceof HTMLElement
      // && druidWebChatState.mergedChatOptions.UI?.closeButtonEnabled 
      && (containerElement.style.display = druidWebChatState.containerElementVisible ? 'initial' : 'none');
  }, [druidWebChatState.containerElementVisible, containerElement, closeButtonEnabled, druidWebChatState.loaded]);

  const calculatedBottom = `calc(${bottom}px + 64px + ${paddingRegular}px)`;
  const ROOT_STYLE = {
    '&.druid__minimizable__web-chat': {
      zIndex: 1051,
      fontFamily: primaryFont,

      '& .druid__minimizable__web-chat__chat-box': {
        backgroundColor: druidWebChatState.INITIALIZATION_STATUS === 'success' ? 'transparent' : backgroundColor,
        border: chatBoxBorderSize,
        borderStyle: 'solid',
        borderColor: chatBoxBorderColor,
        borderRadius: chatBoxBorderRadius,
        borderBottomLeftRadius: chatBoxBottomRadius === 0 ? 0 : chatBoxBottomRadius || chatBoxBorderRadius,
        borderBottomRightRadius: chatBoxBottomRadius === 0 ? 0 : chatBoxBottomRadius || chatBoxBorderRadius,
        boxShadow: '0 0 5px rgba(0, 0, 0, .2)',
        backdropFilter: 'blur(10)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed !important',
        overflow: 'hidden',
        resize: !druidWebChatState.isMobile && !!resizableEnabled && !headerWasDragged && 'horizontal',
        height: closeButtonEnabled ? (typeof height === 'number' ? height - 64 - paddingRegular! : `calc(${height} - ${calculatedBottom})`) : height,
        width: width,
        minWidth: minWidth,
        maxWidth: `calc(100vw - ${typeof floatRight === 'number' ? floatRight + 'px' : floatRight})`,
        bottom: closeButtonEnabled && typeof bottom === 'number' ? calculatedBottom : bottom,
        top: top,
        animationName: 'fadeIn',
        animationDuration: '1s',
        '&.hide': {
          display: 'none'
        },
        '&.left': {
          left: floatRight
        },
        '&.right': {
          right: floatRight
        },
        '& .druid__web-chat__chat-container': {
          fontSize: transcriptFontSize,
          direction: determinedDir
        }
      },
      '.druid__minimizable__web-chat__default-container-element, .druid__minimizable__web-chat__default-container-banner': {
        position: 'fixed',
        right: getSideRTLAware(determinedDir, druidWebChatState.side) === 'right' && floatRight,
        left: getSideRTLAware(determinedDir, druidWebChatState.side) === 'left' && floatRight,
        cursor: 'pointer',
        boxShadow: '0 0 20px rgba(0, 0, 0, .2)',
      },
      '.druid__minimizable__web-chat__default-container-banner': {
        maxWidth: 200,
        bottom: 20 + 100,
        background: 'white',
        padding: paddingRegular,
        border: '1px solid #ccc',
        borderRadius: paddingRegular,
        fontFamily: primaryFont,
        fontSize: "inherit",
        display: "flex",
        flexDirection: "column"
      },
      '.druid__minimizable__web-chat__default-container-banner__first-row': {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 5,
      },
      '.druid__minimizable__web-chat__default-container-banner__dismiss-button': {
        background: 'none',
        border: 'none',
        fontSize: 16,
        lineHeight: 1,
        cursor: 'pointer',
        padding: 0,
        width: 16,
        height: 16,
        marginLeft: paddingRegular
        // verticalAlign: 'middle'
      },
      '.druid__minimizable__web-chat__default-container-element': {
        backgroundColor: accent,
        borderRadius: '50%',
        border: `2px solid ${accent}`,
        bottom: 20,
        color: headerTextColor,
        fontSize: 30,
        height: 64,
        lineHeight: 1,
        // outline: 0,
        padding: 15,

        width: 64,
        cursor: 'pointer',
        '& svg': {
          verticalAlign: 'unset'
        },
        '&_new-messages': {
          height: 16,
          width: 16,
          borderRadius: '50%',
          position: 'absolute',
          top: '-2px',
          right: '-2px',
          backgroundColor: '#ed1c24',
          fontSize: '18px'
        }
      },
    }
  };

  const escapeDeactivatesCallback = useCallback(() => (evnt: KeyboardEvent) => {
    if (!accessibilityFeaturesEnabled) {
      return true;
    }
    const target = evnt.target;
    if (target instanceof HTMLButtonElement && target.classList.contains("webchat__keyboard-help__close-button")) {
      return false;
    }
    return true;
  }, []);

  const rootClassName = druidStyleToEmotionObject(ROOT_STYLE) + '';
  // TODO: [P2] Currently, we cannot unmount Web Chat from DOM when it is minimized.
  //       Today, if we unmount it, Web Chat will call disconnect on DirectLineJS object.
  //       When minimized, we still want to maintain that connection while the UI is gone.
  //       This is related to https://github.com/microsoft/BotFramework-WebChat/issues/2750.

  return (


    <div className={classNames("druid__minimizable__web-chat", rootClassName + '', (druidWebChatState.isMobile || fullScreenMode) && 'druid-is-mobile')}>
      {(!druidWebChatState.minimized || (druidWebChatState.initialized && druidWebChatState.mergedChatOptions.backgroundTrafficEnabled)) && (

        <FocusTrap active={!druidWebChatState.minimized && webchatLoadedRef?.current && !druidWebChatState.isMobile && initialOptions.UI?.withoutRender !== true}
          focusTrapOptions={{ delayInitialFocus: false, allowOutsideClick: true, clickOutsideDeactivates: true, escapeDeactivates: escapeDeactivatesCallback() }}>
          {/* containerElements={[document.querySelector(".druid__minimizable__web-chat")!]} */}
          <Resizable defaultSize={{ width: width as any, height: `to_break` }}
            enable={!resizableEnabled ? false : { top: true, right: getSideRTLAware(determinedDir, druidWebChatState.side) === 'left', bottom: false, left: getSideRTLAware(determinedDir, druidWebChatState.side) === 'right', topRight: false, bottomRight: false, bottomLeft: false, topLeft: false }}
            className={classNames('druid__minimizable__web-chat__chat-box', getSideRTLAware(determinedDir, druidWebChatState.side) === 'left' ? 'left' : 'right', druidWebChatState.minimized ? 'hide' : '')}
            style={{ 'direction': getSideRTLAware(determinedDir, druidWebChatState.side) === 'left' ? 'ltr' : 'rtl' }}
          >
            <LoadableWebchat
              ref={webchatLoadedRef}
              className="druid__web-chat__chat-container"
              initialOptions={initialOptions}
            />
          </Resizable>
        </FocusTrap>

      )}

      {
        druidWebChatState.minimized && druidWebChatState.loaded && !containerElement &&
        <ButtonToggleWebchat>
          <></>
          {/* <svg fill="currentColor" viewBox="0 0 16 16">
            <path d="M16 2a2 2 0 0 0-2-2H2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h9.586a1 1 0 0 1 .707.293l2.853 2.853a.5.5 0 0 0 .854-.353V2zM3.5 3h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1 0-1zm0 2.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1 0-1zm0 2.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1z"></path>
          </svg> */}
        </ButtonToggleWebchat>
      }
      {
        !druidWebChatState.minimized && druidWebChatState.loaded && (closeButtonEnabled && !druidWebChatState.isMobile) &&
        <ButtonToggleWebchat>
          <></>
          {/* <svg stroke="currentColor" fill="currentColor"  viewBox="0 0 16 16">
            <path d="M1.293 1.293a1 1 0 0 1 1.414 0L8 6.586l5.293-5.293a1 1 0 1 1 1.414 1.414L9.414 8l5.293 5.293a1 1 0 0 1-1.414 1.414L8 9.414l-5.293 5.293a1 1 0 0 1-1.414-1.414L6.586 8 1.293 2.707a1 1 0 0 1 0-1.414z"></path>
          </svg> */}
        </ButtonToggleWebchat>
      }

    </div>
  );
});

MinimizableWebChat.defaultProps = { initialOptions: defaultOptions };
// MinimizableWebChat.displayName = "MinimizableWebChat";


const defaultExpandIcon = ({ size }: { size: any }) => {
  return ` <svg fill="currentColor" viewBox="0 0 16 16">
  <path d="M16 2a2 2 0 0 0-2-2H2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h9.586a1 1 0 0 1 .707.293l2.853 2.853a.5.5 0 0 0 .854-.353V2zM3.5 3h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1 0-1zm0 2.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1 0-1zm0 2.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1z"></path>
</svg>`;
};
const defaultCollapseIcon = ({ size }: { size: any }) => {
  return `<svg stroke="currentColor" fill="currentColor"  viewBox="0 0 16 16">
  <path d="M1.293 1.293a1 1 0 0 1 1.414 0L8 6.586l5.293-5.293a1 1 0 1 1 1.414 1.414L9.414 8l5.293 5.293a1 1 0 0 1-1.414 1.414L8 9.414l-5.293 5.293a1 1 0 0 1-1.414-1.414L6.586 8 1.293 2.707a1 1 0 0 1 0-1.414z"></path>
</svg>`;
};
const ButtonToggleWebchat = ({ children }) => {
  const [druidWebChatState, druidWebChatDispatch, ,] = useContext<DruidWebchatContextType>(DruidWebchatContext);
  const { UI: { v2_containerElement_expand_icon = "", v2_containerElement_collapse_icon = "", draggableEnabled, rootFontSize,
    containerElementBannerEnabled, containerElementBannerDismissButtonEnabled, containerElementFirstBannerRowHtml, containerElementSecondBannerRowHtml, containerElementBannerPersistDismiss, containerElementBannerDelayInSeconds
  } = {},
    styleOptions: { primaryFont, bubbleBorderRadius, paddingRegular } = {}
  } = useMergedOptions();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [onDragStart] = useDraggableEffect(draggableEnabled, buttonRef);

  const [show, setShow] = useState(true);
  const [visibleByDelay, setVisibleByDelay] = useState(false)
  const isBannerNotDismissed = !containerElementBannerPersistDismiss || !sessionStorage.getItem('druidBannerIsDismissed')

  const sanitized_containerElementFirstBannerRowHtml = useMemo(() => DOMPurify.sanitize(containerElementFirstBannerRowHtml || "", { USE_PROFILES: { html: true } }), [containerElementFirstBannerRowHtml]);
  const sanitized_containerElementSecondBannerRowHtml = useMemo(() => DOMPurify.sanitize(containerElementSecondBannerRowHtml || "", { USE_PROFILES: { html: true } }), [containerElementSecondBannerRowHtml]);


  useEffect(() => {
    let timer: any = undefined;
    if(!visibleByDelay) {
      timer = setTimeout(() => {
        setVisibleByDelay(true);
      }, (containerElementBannerDelayInSeconds  || 0) * 1000);
    }
   
    return () => timer && clearTimeout(timer);
  }, [visibleByDelay]);

  return (
    <>
      {  isBannerNotDismissed && visibleByDelay && containerElementBannerEnabled && druidWebChatState.minimized && !druidWebChatState.initialized && show && 
      <div className="druid__minimizable__web-chat__default-container-banner"
        onClick={() => druidWebChatDispatch({ type: druidWebChatState.minimized ? 'OPEN_WEBCHAT' : 'CLOSE_WEBCHAT' })} aria-label={"Banner to open chat"}
        tabIndex={0} role="button"
      >
        <div className="druid__minimizable__web-chat__default-container-banner__first-row">
          {
          containerElementFirstBannerRowHtml && 
            <div className="druid__minimizable__web-chat__default-container-banner__first-row__html" dangerouslySetInnerHTML={{ __html: sanitized_containerElementFirstBannerRowHtml }}>
            </div>
          }
          {
          containerElementBannerDismissButtonEnabled &&
              <button className="druid__minimizable__web-chat__default-container-banner__dismiss-button" aria-label={'Dismiss banner'} tabIndex={0}
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setShow(false); 
                  containerElementBannerPersistDismiss && sessionStorage.setItem('druidBannerIsDismissed', "true")
                }}
            >
            <FaTimes></FaTimes>
          </button>
        }
        </div>
        {
        containerElementSecondBannerRowHtml && 
          <div className="druid__minimizable__web-chat__default-container-banner__second-row" dangerouslySetInnerHTML={{ __html: sanitized_containerElementSecondBannerRowHtml }}>
          </div>
        }
      </div>
      }
      <button className="druid__minimizable__web-chat__default-container-element" style={{ cursor: draggableEnabled ? 'move' : 'pointer' }} ref={buttonRef} id="druid__default_container-element"
        onDragStart={onDragStart} draggable={!!draggableEnabled}
        onClick={() => druidWebChatDispatch({ type: druidWebChatState.minimized ? 'OPEN_WEBCHAT' : 'CLOSE_WEBCHAT' })} aria-label={druidWebChatState.minimized ? 'Open chat' : 'Close chat'}>
        {druidWebChatState.minimized &&
          <>

            <InlineSVG src={typeof v2_containerElement_expand_icon === "function" ? v2_containerElement_expand_icon({ size: 30 }) : (v2_containerElement_expand_icon || defaultExpandIcon({ size: 30 }))} raw={true} >
            </InlineSVG>
          </>

        }
        {!druidWebChatState.minimized &&
          <InlineSVG src={typeof v2_containerElement_collapse_icon === "function" ? v2_containerElement_collapse_icon({ size: 30 }) : (v2_containerElement_collapse_icon || defaultCollapseIcon({ size: 30 }))} raw={true} >
          </InlineSVG>
        }
        {druidWebChatState.newMessage && <span className="ms-Icon druid__minimizable__web-chat__default-container-element_new-messages" />}
      </button>
    </>
  );
}

export default MinimizableWebChat;
